import type { Product } from '../types';
import { brandifyText } from '../lib/utils';

/**
 * Display-only brand presentation: wrap the brand token in guillemets
 * («X-1493») for every product fetched, so all customer-facing pages
 * (Store / Home / ProductDetail / Cart / Checkout) show it consistently.
 * Stored data (D1, SKU, slugs) is untouched.
 */
function brandifyProduct(p: Product): Product {
  return {
    ...p,
    name: p.name ? brandifyText(p.name) : p.name,
    description: p.description ? brandifyText(p.description) : p.description,
    brand: p.brand ? brandifyText(p.brand) : p.brand,
  };
}

// Kare：後備產品清空（2026-10-19）——x1493 原本此處有 3 件辣粉示範作 API 失效後備，
// 對 Kare 屬另一客戶內容，寧願顯示空白都唔可以漏。產品一律以 D1 真實數據為準。
export const DEMO_PRODUCTS: Product[] = [];

export async function fetchProducts(): Promise<Product[]> {
  try {
    const res = await fetch('/api/public/products');
    if (!res.ok) return DEMO_PRODUCTS.map(brandifyProduct);
    const json = await res.json();
    if (json.success && Array.isArray(json.data) && json.data.length > 0) {
      return json.data.map((p: Product) => brandifyProduct({
        ...p,
        name: p.name || `商品 #${p.id}`,
        slug: p.slug || (p.name ? p.name.toLowerCase().replace(/\s+/g, '-') : `product-${p.id}`),
        description: p.description || '',
        suitableDishes: p.suitableDishes || [],
      }));
    }
    return DEMO_PRODUCTS.map(brandifyProduct);
  } catch {
    return DEMO_PRODUCTS.map(brandifyProduct);
  }
}

export async function fetchProductBySlug(slug: string): Promise<Product | null> {
  const products = await fetchProducts();
  // Names now carry «X-1493»; normalize (strip «», lowercase, spaces->dashes)
  // so product links shared before the rename still resolve.
  const norm = (s: string) =>
    decodeURIComponent(s).toLowerCase().replace(/[«»]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
  const target = norm(slug);
  return (
    products.find((p) => p.slug === slug) ||
    products.find((p) => p.name === slug) ||
    products.find((p) => norm(p.slug) === target) ||
    products.find((p) => norm(p.name) === target) ||
    null
  );
}
